// Approval Settings — admin control for the purchase-order approval gate.
// One card, one job: pick who approves, and the dollar line they approve
// above. Rarely touched, so the page favors clarity over density.
//
// GET/PUT /api/procurement/approval-settings. Money travels the wire as
// integer cents (approval_threshold_cents); this page is the boundary that
// converts to/from a plain dollar figure for the input — same convention as
// formatCents in Procurement/poApi.ts, just inlined here since this isn't a
// PO-shaped read.

import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import HelpDrawer from '../components/HelpDrawer';
import { useRequisitionsApi } from '../Requisitions/requisitionsApi';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

interface ApprovalSettingsResponse {
  approver_user_ids: string[];
  approval_threshold_cents: number;
  /** Percent over the ordered quantity a receive may land without an admin.
   *  0 means every over-receipt escalates, which is the intended default. */
  over_receipt_pct: number;
}

interface UserRow {
  id: string;
  firstname: string;
  lastname: string | null;
  email: string;
  deactivated_at: string | null;
  is_service: boolean;
}

const centsToDollarsStr = (cents: number): string => (cents / 100).toFixed(2);

/** Returns null for anything that isn't a valid non-negative dollar amount. */
const dollarsStrToCents = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

// ── Requests (requisitions) ─────────────────────────────────────────────────
// Drawn in the surface tokens; the purchase-order card above predates them.

const RequestsUserList: React.FC<{
  id: string;
  title: string;
  users: UserRow[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  emptyNote: string;
  singleNote: string;
}> = ({ id, title, users, selectedIds, onToggle, emptyNote, singleNote }) => {
  const picked = users.filter((u) => selectedIds.includes(u.id));
  return (
    <fieldset aria-describedby={`${id}-note`} className="min-w-0">
      <legend className="text-sm font-semibold text-on-surface">{title}</legend>
      <Reveal show={picked.length > 0}>
        <div className="flex flex-wrap gap-1.5 pt-2">
          {picked.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary-ink"
            >
              {u.firstname}
            </span>
          ))}
        </div>
      </Reveal>
      <FadeIn key={picked.length === 1 ? 'single' : 'plain'} className="mt-2">
        {picked.length === 1 ? (
          <div id={`${id}-note`}>
            <Alert tone="warning" variant="note" live={false} className="!flex w-full">{singleNote}</Alert>
          </div>
        ) : (
          <p id={`${id}-note`} className="rounded-md bg-surface-container px-3 py-2 text-sm text-on-surface-variant">
            {picked.length === 0 ? emptyNote : `${picked.length} people. Any one of them can act.`}
          </p>
        )}
      </FadeIn>
      <div className="mt-2 max-h-60 overflow-y-auto rounded-md bg-surface-container">
        {users.length === 0 ? (
          <EmptyState variant="area">No active users found.</EmptyState>
        ) : (
          users.map((u) => {
            const checked = selectedIds.includes(u.id);
            return (
              <Checkbox
                key={u.id}
                fill
                checked={checked}
                onChange={() => onToggle(u.id)}
                className={`gap-3 px-3.5 py-2 transition-colors duration-150 ease-emphasized motion-reduce:transition-none ${
                  checked ? 'bg-primary/10' : 'hover:bg-surface-container-high'
                }`}
                label={
                  <span className="flex min-w-0 items-center gap-3">
                    <span>{u.firstname}</span>
                    <span className="truncate text-xs text-on-surface-variant">{u.email}</span>
                  </span>
                }
              />
            );
          })
        )}
      </div>
    </fieldset>
  );
};

const ApprovalSettings: React.FC = () => {
  const api = useApi();
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  // This configures who can sign off on money leaving the building — kept to
  // admins, not opened to can_procurement (mirrors /admin/cards' pattern of a
  // tighter gate than the surface it configures).
  const isAdmin = user.isadmin || user.can_admin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [thresholdInput, setThresholdInput] = useState('');
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [allowanceInput, setAllowanceInput] = useState('');
  const [allowanceError, setAllowanceError] = useState<string | null>(null);
  const [reqApproverIds, setReqApproverIds] = useState<string[]>([]);
  const [reqOrdererIds, setReqOrdererIds] = useState<string[]>([]);
  const [reqLoadFailed, setReqLoadFailed] = useState(false);
  const [reqSaving, setReqSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoading(true);
      try {
        const [settings, allUsers] = await Promise.all([
          api.get('/procurement/approval-settings') as Promise<ApprovalSettingsResponse>,
          api.get('/users', { silent: true }) as Promise<UserRow[]>,
        ]);
        setApproverIds(settings.approver_user_ids || []);
        setThresholdInput(centsToDollarsStr(settings.approval_threshold_cents ?? 0));
        setAllowanceInput(String(settings.over_receipt_pct ?? 0));
        setUsers(allUsers || []);
      } catch (err: any) {
        toast.error(err?.data?.error?.message || err?.message || 'Failed to load approval settings');
      } finally {
        setLoading(false);
      }
    })();
    // Separate on purpose: a failure here must not hide the purchase-order card.
    reqApi
      .getSettings()
      .then((rs) => {
        setReqApproverIds(rs.approver_user_ids || []);
        setReqOrdererIds(rs.orderer_user_ids || []);
        setReqLoadFailed(false);
      })
      .catch(() => setReqLoadFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <Redirect to="/" />;

  const activeUsers = users
    .filter((u) => !u.deactivated_at && !u.is_service)
    .sort((a, b) => a.firstname.localeCompare(b.firstname));

  const selectedApprovers = activeUsers.filter((u) => approverIds.includes(u.id));

  const toggleApprover = (id: string) => {
    setApproverIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (id: string) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSaveRequests = async () => {
    setReqSaving(true);
    try {
      const saved = await reqApi.putSettings({
        approver_user_ids: reqApproverIds,
        orderer_user_ids: reqOrdererIds,
      });
      setReqApproverIds(saved.approver_user_ids || []);
      setReqOrdererIds(saved.orderer_user_ids || []);
      toast.success('Request settings saved');
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Could not save the request settings. Try again.');
    } finally {
      setReqSaving(false);
    }
  };

  const handleThresholdChange = (value: string) => {
    setThresholdInput(value);
    setThresholdError(null);
  };

  const handleSave = async () => {
    const cents = dollarsStrToCents(thresholdInput);
    if (cents === null) {
      setThresholdError('Enter a dollar amount of 0 or more.');
      return;
    }
    const pct = Number(allowanceInput.trim());
    if (!allowanceInput.trim() || !Number.isFinite(pct) || pct < 0 || pct > 100) {
      setAllowanceError('Enter a percentage from 0 to 100. Zero sends every over-receipt to an admin.');
      return;
    }
    setSaving(true);
    try {
      const updated = (await api.put('/procurement/approval-settings', {
        body: {
          approver_user_ids: approverIds,
          approval_threshold_cents: cents,
          over_receipt_pct: pct,
        },
      })) as ApprovalSettingsResponse;
      setApproverIds(updated.approver_user_ids || []);
      setThresholdInput(centsToDollarsStr(updated.approval_threshold_cents ?? cents));
      setAllowanceInput(String(updated.over_receipt_pct ?? pct));
      toast.success('Approval settings saved');
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout kind="focus" className="pt-4">
<div className="space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold text-primary">Approval Settings</h1>
        <HelpDrawer title="About approval settings">
          <p className="text-on-surface font-medium">What this controls</p>
          <p>
            Purchase orders at or above the dollar threshold below need a person to
            approve them before they go out to the vendor. Anything under the
            threshold goes straight through.
          </p>
          <p className="text-on-surface font-medium">Who can approve</p>
          <p>
            Only the people picked on this page. When a purchase order needs
            approval, every approver gets a Slack DM and an email. The list is
            exclusive: once someone is picked, even an admin who is not on the
            list cannot approve.
          </p>
          <p className="text-on-surface font-medium">Nobody picked yet</p>
          <p>
            With no approvers set, this is bootstrap mode: any admin can approve
            in the meantime, so the workflow never gets stuck with nobody able
            to say yes.
          </p>
          <p className="text-on-surface font-medium">Over-receipt allowance</p>
          <p>
            Vendors sometimes ship a little extra. Within this percentage the
            receiving hub books the extra into stock and flags it; past it, an
            admin must receive it. Zero is the strict setting: any extra unit at
            all waits for an admin. Raise it only if a vendor routinely ships
            overage you are happy to keep.
          </p>
          <p className="text-on-surface font-medium">Requests</p>
          <p>
            Requests are how anyone asks for supplies and other things that are not a
            purchase order. Every request needs a yes from someone on the approver list,
            and someone on the buyer list then buys it on a company card.
          </p>
          <p>
            An empty list means any admin can do that job. Once you name someone, only
            the people named can, admins included.
          </p>
          <p>
            One name on a list means nobody covers for that person. While they are away,
            requests wait. Add a second name to keep things moving.
          </p>
        </HelpDrawer>
      </div>

      <FadeIn key={loading ? 'loading' : 'loaded'}>
      {loading ? (
        <LogoSpinner label="Loading approval settings" />
      ) : (
        <Panel label="Purchase order approval" padding="md">
          <div className="space-y-6">
          {/* Approvers */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
              Purchase orders over the threshold need approval from:
            </div>

            <FadeIn key={selectedApprovers.length > 0 ? 'named' : 'none'}>
            {selectedApprovers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {selectedApprovers.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center rounded-full bg-primary/15 text-primary-ink ring-1 ring-inset ring-primary/30 px-2.5 py-0.5 text-xs font-medium"
                  >
                    {u.firstname}
                  </span>
                ))}
              </div>
            ) : (
              <Alert tone="warning" live={false} className="mb-3">
                No named approvers set. Any admin can approve. Pick at least one
                person to make this a named-person control.
              </Alert>
            )}
            </FadeIn>

            <div className="max-h-72 overflow-y-auto rounded-md bg-surface-container divide-y divide-outline-variant">
              {activeUsers.length === 0 ? (
                <EmptyState variant="area">No active users found.</EmptyState>
              ) : (
                activeUsers.map((u) => {
                  const checked = approverIds.includes(u.id);
                  return (
                    <Checkbox
                      key={u.id}
                      fill
                      checked={checked}
                      onChange={() => toggleApprover(u.id)}
                      className={`gap-3 px-3.5 py-2 transition-colors ${
                        checked ? 'bg-primary/10' : 'hover:bg-surface-container-high'
                      }`}
                      label={
                        <span className="flex min-w-0 items-center gap-3">
                          <span>{u.firstname}</span>
                          <span className="text-xs text-on-surface-variant truncate">{u.email}</span>
                        </span>
                      }
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* Threshold */}
          <div>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
                Approval threshold
              </span>
              <div className="relative max-w-[10rem]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm">$</span>
                <Field
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={thresholdInput}
                  onChange={(e) => handleThresholdChange(e.target.value)}
                  aria-describedby="threshold-help"
                  error={!!thresholdError}
                  className="!pl-6"
                />
              </div>
            </label>
            <p id="threshold-help" className="text-xs text-on-surface-variant mt-1.5">
              Purchase orders at or above this amount wait for approval.
            </p>
            <Reveal show={!!thresholdError}><div className="pt-1.5"><Alert tone="error" variant="text">{thresholdError}</Alert></div></Reveal>
          </div>

          {/* Over-receipt allowance. Same kind of number as the threshold: how
              much passes without a named person saying yes. */}
          <div>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
                Over-receipt allowance
              </span>
              <div className="relative max-w-[10rem]">
                <Field
                  type="number"
                  min={0}
                  max={100}
                  step="0.5"
                  inputMode="decimal"
                  value={allowanceInput}
                  onChange={(e) => { setAllowanceInput(e.target.value); setAllowanceError(null); }}
                  aria-describedby="allowance-help"
                  error={!!allowanceError}
                  className="!pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm">%</span>
              </div>
            </label>
            <p id="allowance-help" className="text-xs text-on-surface-variant mt-1.5">
              Deliveries up to this much over the ordered quantity go straight into
              stock. At zero, an admin must receive over-quantities.
            </p>
            <Reveal show={!!allowanceError}><div className="pt-1.5"><Alert tone="error" variant="text">{allowanceError}</Alert></div></Reveal>
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={buttonClass('filled')}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          </div>
        </Panel>
      )}
      </FadeIn>

      {!loading && (
        <FadeIn>
        <Panel title="Requests">
          <div className="space-y-5">
          <div>
            <p className="text-sm text-on-surface-variant">
              Who approves the things people ask for, and who buys them once approved. An empty
              list means any admin.
            </p>
          </div>
          {reqLoadFailed ? (
            <Alert tone="error">
              Could not load the request lists. Reload the page to try again.
            </Alert>
          ) : (
            <>
              <div className="grid gap-5 md:grid-cols-2">
                <RequestsUserList
                  id="req-approvers"
                  title="Approve requests"
                  users={activeUsers}
                  selectedIds={reqApproverIds}
                  onToggle={toggleIn(setReqApproverIds)}
                  emptyNote="Nobody named, so any admin can approve."
                  singleNote="One name: while they are away, nothing gets approved. Add a second person to cover."
                />
                <RequestsUserList
                  id="req-orderers"
                  title="Buy approved requests"
                  users={activeUsers}
                  selectedIds={reqOrdererIds}
                  onToggle={toggleIn(setReqOrdererIds)}
                  emptyNote="Nobody named, so any admin can buy."
                  singleNote="One name: while they are away, nothing gets bought. Add a second person to cover."
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveRequests}
                  disabled={reqSaving}
                  className={buttonClass('quiet')}
                >
                  {reqSaving ? 'Saving…' : 'Save request lists'}
                </button>
              </div>
            </>
          )}
          </div>
        </Panel>
        </FadeIn>
      )}
    </div>
</PageLayout>
  );
};

export default ApprovalSettings;
